import React, { useEffect, useRef, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { initMercadoPago, Payment } from '@mercadopago/sdk-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { ArrowLeft, ShieldCheck, Loader2, CheckCircle2, XCircle, Clock, CreditCard, Lock, Timer, Mail } from 'lucide-react';
import { trackPurchase, trackInitiateCheckout, setAdvancedMatchingEmail } from '@/lib/metaPixel';
import { datosDeAtribucion } from '@/lib/atribucion';
import { CURRENT_PRICE_ARS, PACK_PRICE_ARS, PACK_ENTREVISTAS } from '@/config/pricing';
import { api } from '@/utils/api';

const API = process.env.REACT_APP_BACKEND_URL;
const PUBLIC_KEY = process.env.REACT_APP_MERCADOPAGO_PUBLIC_KEY;

// Inicializar el SDK solo una vez (top-level, fuera del render)
let mpInitialized = false;
function ensureMpInit() {
  if (mpInitialized || !PUBLIC_KEY) return;
  initMercadoPago(PUBLIC_KEY, { locale: 'es-AR' });
  mpInitialized = true;
}

function formatARS(amount) {
  return `$ ${Number(amount).toLocaleString('es-AR', { minimumFractionDigits: 0 })}`;
}

// 15 minutos de "reserva de precio" — sólo UI, no bloquea el pago real
const RESERVATION_MS = 15 * 60 * 1000;

// Componente AISLADO para el contador. Su re-render por segundo NO afecta al padre,
// con eso evitamos que el Brick de MercadoPago se desmonte cada tick.
function ReservationCountdown() {
  const [remaining, setRemaining] = useState(RESERVATION_MS);
  useEffect(() => {
    const endsAt = Date.now() + RESERVATION_MS;
    const tick = () => {
      const left = endsAt - Date.now();
      setRemaining(left > 0 ? left : 0);
    };
    tick();
    const id = setInterval(tick, 1000);
    return () => clearInterval(id);
  }, []);
  const totalSec = Math.max(0, Math.floor(remaining / 1000));
  const mm = String(Math.floor(totalSec / 60)).padStart(2, '0');
  const ss = String(totalSec % 60).padStart(2, '0');
  return (
    <strong className="text-[#5B9B2F] font-mono tabular-nums" data-testid="checkout-countdown">
      {mm}:{ss}
    </strong>
  );
}

// Wrapper memoizado del Payment Brick: aunque el padre re-renderice por otras razones,
// si las props clave (amount, externalReference, email, callbacks) NO cambiaron, el
// Brick NO se vuelve a montar. Esto es lo que evita el "parpadeo" del checkout.
//
// Sobre el email: en el nuevo flujo (sin email-gate) el email PUEDE venir vacío.
// Cuando llega vacío NO lo pasamos a la initialization; así el Brick renderiza su
// propio campo email como primer input del formulario. Cuando ya tenemos email
// (prefill desde localStorage), lo pasamos para que el Brick lo skipee.
const MemoizedPaymentBrick = React.memo(function MemoizedPaymentBrick({
  amount, email, onSubmit, onError, onReady,
}) {
  const trimmed = (email || '').trim();
  const payer = trimmed
    ? { email: trimmed, entityType: 'individual' }
    : { entityType: 'individual' };
  return (
    <Payment
      initialization={{
        amount,
        payer,
      }}
      customization={{
        paymentMethods: {
          creditCard: 'all',
          debitCard: 'all',
          maxInstallments: 3,
        },
        visual: { style: { theme: 'default' }, hidePaymentButton: false },
      }}
      onSubmit={onSubmit}
      onError={onError}
      onReady={onReady}
    />
  );
});

// Producto a comprar: ?producto=pack o el ilimitado (por defecto). Es solo lo
// que se PIDE: el servidor guarda el producto en la sesion de checkout y
// calcula el precio; el navegador nunca decide cuanto se cobra.
function productoDeLaUrl() {
  try {
    return new URL(window.location.href).searchParams.get('producto') === 'pack' ? 'pack' : 'ilimitado';
  } catch {
    return 'ilimitado';
  }
}

export default function CheckoutPage() {
  const navigate = useNavigate();
  const [producto] = useState(productoDeLaUrl);
  const [externalReference, setExternalReference] = useState(null);
  const [amount, setAmount] = useState(() => (producto === 'pack' ? PACK_PRICE_ARS : CURRENT_PRICE_ARS));
  const [nombreProducto, setNombreProducto] = useState(
    () => (producto === 'pack' ? `Pack de ${PACK_ENTREVISTAS} entrevistas` : 'EVALPRO ilimitado'),
  );
  // Con la sesion iniciada, la compra es para esa cuenta: el mail viene fijo.
  const [emailBloqueado, setEmailBloqueado] = useState(null);
  // Precio para el mail escrito: { estado, amount, mensaje, descuento, email }.
  // El formulario de pago se muestra solo con una cotizacion "ok" para ESE mail.
  const [cotizacion, setCotizacion] = useState(null);
  const [cotizando, setCotizando] = useState(false);
  // Mail a cotizar: se actualiza con la misma espera de medio segundo que
  // valida el mail, para no pedir un precio por cada tecla.
  const [emailParaCotizar, setEmailParaCotizar] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [result, setResult] = useState(null); // { status, status_detail, payment_id }

  // Email gate (capturado antes del Brick para Advanced Matching + UX)
  const [email, setEmail] = useState(() => {
    try {
      return (typeof window !== 'undefined' && window.localStorage.getItem('evalpro_checkout_email')) || '';
    } catch { return ''; }
  });
  // Se pone en true cuando el Brick termina de renderizar (onReady).
  // Mientras es false, mostramos un loader visible arriba del skeleton gris del SDK.
  const [brickReady, setBrickReady] = useState(false);
  // Email sintácticamente válido (con debounce). El Brick se monta cuando esto pasa a true.
  const [emailValid, setEmailValid] = useState(false);

  const fbEventIdRef = useRef(null);
  const idempotencyKeyRef = useRef(null);

  useEffect(() => {
    ensureMpInit();
  }, []);

  // is_test: ?test=1 en URL marca esta sesión como prueba interna (no contamina el funnel).
  // Persiste en localStorage para que sobreviva al re-render del Brick.
  const isTestRef = useRef(false);
  useEffect(() => {
    try {
      const url = new URL(window.location.href);
      const urlFlag = url.searchParams.get('test') === '1';
      const stored = window.localStorage.getItem('evalpro_checkout_is_test') === '1';
      isTestRef.current = urlFlag || stored;
      if (urlFlag) window.localStorage.setItem('evalpro_checkout_is_test', '1');
    } catch { /* noop */ }
  }, []);

  // Llamada inicial: registrar "checkout iniciado" + obtener external_reference
  useEffect(() => {
    let cancelled = false;
    async function start() {
      try {
        // Si hay sesion iniciada se manda el token: la compra queda para esa
        // cuenta y el servidor aplica el descuento de lo pagado en packs.
        const headers = { 'Content-Type': 'application/json' };
        const token = api.getToken();
        if (token) headers.Authorization = `Bearer ${token}`;
        const res = await fetch(`${API}/api/payments/mp/checkout-started`, {
          method: 'POST',
          headers,
          body: JSON.stringify({ is_test: isTestRef.current, producto }),
        });
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const data = await res.json();
        if (cancelled) return;
        setExternalReference(data.external_reference);
        setAmount(data.amount);
        if (data.nombre_producto) setNombreProducto(data.nombre_producto);
        if (data.email_bloqueado) {
          setEmailBloqueado(data.email_bloqueado);
          setEmail(data.email_bloqueado);
          setCotizacion({
            estado: data.estado,
            amount: data.amount,
            mensaje: data.mensaje,
            descuento: data.descuento,
            email: data.email_bloqueado,
          });
        }

        // event_id único para deduplicar Pixel ↔ CAPI server-side
        fbEventIdRef.current = (typeof crypto !== 'undefined' && crypto.randomUUID)
          ? crypto.randomUUID()
          : `${Date.now()}-${Math.random().toString(36).slice(2)}`;
        // idempotency-key único por carga del checkout
        idempotencyKeyRef.current = (typeof crypto !== 'undefined' && crypto.randomUUID)
          ? crypto.randomUUID()
          : `${Date.now()}-${Math.random().toString(36).slice(2)}`;
      } catch (e) {
        if (!cancelled) setError('No pudimos preparar el checkout. Recargá la página.');
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    start();
    return () => { cancelled = true; };
  }, []);

  // ViewContent va solo en la landing (la página de venta): acá se contaba
  // dos veces por visita.

  // Detecta email válido con debounce de 500ms. Cuando pasa a válido:
  // - persiste en localStorage
  // - envía Advanced Matching (hash) a Meta
  // - habilita el mount del Brick (via emailValid=true)
  // No dispara InitiateCheckout todavía; ese evento vive en handleBrickReady.
  const advancedMatchingFiredRef = useRef('');
  useEffect(() => {
    const trimmed = (email || '').trim().toLowerCase();
    const isSyntacticallyValid = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(trimmed);
    if (!isSyntacticallyValid) {
      if (emailValid) setEmailValid(false);
      return;
    }
    const t = setTimeout(() => {
      setEmailValid(true);
      setEmailParaCotizar(trimmed);
      // Advanced Matching solo una vez por email
      if (advancedMatchingFiredRef.current !== trimmed) {
        advancedMatchingFiredRef.current = trimmed;
        try { window.localStorage.setItem('evalpro_checkout_email', trimmed); } catch { /* noop */ }
        try { setAdvancedMatchingEmail(trimmed); } catch { /* noop */ }
      }
    }, 500);
    return () => clearTimeout(t);
  }, [email, emailValid]);

  // Precio para el mail escrito. Sin sesion iniciada, el servidor dice si ese
  // mail ya tiene el ilimitado (no se le vende de nuevo) o si compro packs y
  // tiene que iniciar sesion para el descuento.
  useEffect(() => {
    if (emailBloqueado || !emailParaCotizar || !externalReference) return undefined;
    const emailNormalizado = emailParaCotizar;
    let cancelado = false;
    setCotizando(true);
    fetch(`${API}/api/payments/mp/cotizar`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ external_reference: externalReference, email: emailNormalizado }),
    })
      .then((r) => (r.ok ? r.json() : Promise.reject(new Error(`HTTP ${r.status}`))))
      .then((c) => {
        if (cancelado) return;
        setCotizacion({ ...c, email: emailNormalizado });
        setAmount(c.amount);
        if (c.nombre_producto) setNombreProducto(c.nombre_producto);
      })
      .catch(() => {
        if (!cancelado) setError('No pudimos calcular el precio. Recargá la página.');
      })
      .finally(() => {
        if (!cancelado) setCotizando(false);
      });
    return () => { cancelado = true; };
  }, [emailParaCotizar, emailBloqueado, externalReference]);

  // Funnel Step 2 (nuevo esquema de 3 pasos): cuando el Brick termina de renderizar,
  // registramos "Vio formulario de pago". El email ya NO es un paso previo — el propio
  // Brick captura el email como primer campo del formulario.
  const brickReadyFiredRef = useRef(false);
  const handleBrickReady = () => {
    // Oculta el overlay de carga que cubre el skeleton gris del SDK de MP.
    setBrickReady(true);
    if (brickReadyFiredRef.current || !externalReference) return;
    brickReadyFiredRef.current = true;

    // InitiateCheckout: si se entró sin pasar por el botón de la landing (mail,
    // banner de prueba), se marca acá. Una vez por pestaña (ver metaPixel.js).
    // Sin eventID: el de esta carga queda para el Purchase.
    try {
      trackInitiateCheckout({
        value: amount,
        currency: 'ARS',
        content_name: nombreProducto,
        content_type: 'product',
        content_ids: [producto],
      });
    } catch { /* noop */ }
    try {
      fetch(`${API}/api/payments/mp/checkout-brick-ready`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          external_reference: externalReference,
          is_test: isTestRef.current,
        }),
      }).catch(() => {});
    } catch { /* noop */ }
  };

  const handleSubmit = async ({ formData, selectedPaymentMethod }) => {
    try {
      // Prioridad: email confirmado por el usuario; fallback al que MP pida en el Brick
      const payerEmail = ((email || formData.payer?.email) || '').trim().toLowerCase();

      // Persistir + Advanced Matching en el momento del submit (antes hacíamos esto en
      // el email-gate, que ya no existe). Fire-and-forget: no bloqueamos el pago.
      if (payerEmail && payerEmail !== email) {
        try { setEmail(payerEmail); } catch { /* noop */ }
        try { window.localStorage.setItem('evalpro_checkout_email', payerEmail); } catch { /* noop */ }
        try { await setAdvancedMatchingEmail(payerEmail); } catch { /* noop */ }
        // Registrar el email en la preferencia para reporting histórico (deprecado como paso,
        // pero seguimos guardando el timestamp por si alguna vez querés analizarlo).
        try {
          fetch(`${API}/api/payments/mp/checkout-email-provided`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              external_reference: externalReference,
              email: payerEmail,
              is_test: isTestRef.current,
            }),
            keepalive: true,
          }).catch(() => {});
        } catch { /* noop */ }
      }

      const body = {
        token: formData.token,
        installments: formData.installments || 1,
        payment_method_id: formData.payment_method_id || (selectedPaymentMethod && selectedPaymentMethod.id),
        issuer_id: formData.issuer_id || undefined,
        external_reference: externalReference,
        payer: {
          email: payerEmail,
          identification: formData.payer?.identification || undefined,
        },
        fb_event_id: fbEventIdRef.current,
        // fbp/fbc y UTM de la llegada, para el Purchase del servidor.
        atribucion: datosDeAtribucion(),
      };

      const res = await fetch(`${API}/api/payments/mp/pay`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Idempotency-Key': idempotencyKeyRef.current,
          // Identificación del dispositivo (security.js, en index.html): Mercado
          // Pago la usa para no rechazar pagos por riesgo.
          ...(window.MP_DEVICE_SESSION_ID ? { 'X-meli-session-id': window.MP_DEVICE_SESSION_ID } : {}),
        },
        body: JSON.stringify(body),
      });
      const data = await res.json();
      if (!res.ok) {
        throw new Error(data.detail || data.message || `HTTP ${res.status}`);
      }

      setResult({
        status: data.status,
        status_detail: data.status_detail,
        payment_id: data.payment_id,
      });

      // Purchase solo con el pago aprobado por Mercado Pago (la respuesta del
      // servidor), con el mismo eventID que usa el webhook para la API de
      // Conversiones. El valor es lo cobrado (39.900 el ilimitado).
      if (data.status === 'approved') {
        trackPurchase(
          {
            value: amount,
            currency: 'ARS',
            content_name: nombreProducto,
            content_ids: [producto],
            content_type: 'product',
          },
          { eventID: fbEventIdRef.current }
        );
      }

      return Promise.resolve();
    } catch (e) {
      setResult({ status: 'error', status_detail: e.message || 'Error desconocido' });
      return Promise.reject(e);
    }
  };

  // Stable callbacks: el Brick recibe SIEMPRE la misma referencia entre renders
  // (vía ref). Las refs se actualizan dentro de useEffect para cumplir con la regla
  // "no escribir refs durante el render".
  const submitRef = useRef(handleSubmit);
  const readyRef = useRef(handleBrickReady);
  const errorRef = useRef(null);
  useEffect(() => {
    submitRef.current = handleSubmit;
    readyRef.current = handleBrickReady;
    errorRef.current = (e) => {
      // El SDK del Brick pasa el error con distintas formas según el momento:
      //  - init/config: string o objeto con .cause/.message
      //  - runtime/submit: objeto con .cause/.message/.type
      // Persistimos TODO en backend para poder diagnosticar el error real de MP.
      console.error('[Brick error]', e);
      let phase = 'runtime';
      if (!brickReadyFiredRef.current) phase = 'init';
      const payload = {
        external_reference: externalReference || null,
        email: email || null,
        type: typeof e,
        cause: (e && (e.cause || e.error || e.type)) ?? null,
        message: (e && (e.message || e.reason)) ?? String(e),
        raw: (() => {
          try {
            if (e instanceof Error) {
              return { name: e.name, message: e.message, stack: (e.stack || '').slice(0, 500) };
            }
            return e;
          } catch (_) { return String(e); }
        })(),
        phase,
      };
      try {
        // Fire-and-forget; nunca bloquea el UX
        fetch(`${API}/api/payments/mp/brick-error`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(payload),
          keepalive: true,
        }).catch(() => {});
      } catch (_) { /* noop */ }
      // Los errores "no críticos" (por ejemplo, un número de tarjeta que
      // Mercado Pago no reconoce) los muestra el propio formulario junto al
      // campo, y el formulario sigue andando. Antes cualquier error lo
      // reemplazaba por "Recargá la página": un dígito mal tipeado tiraba abajo
      // el checkout. Mientras el formulario carga sí se avisa: si no, quedaría
      // "Cargando tu forma de pago" para siempre.
      if (e && e.type === 'non_critical' && brickReadyFiredRef.current) return;
      setError('Hubo un error con el formulario de pago. Recargá la página.');
    };
  });
  const stableSubmit = useCallback((args) => submitRef.current && submitRef.current(args), []);
  const stableReady = useCallback(() => readyRef.current && readyRef.current(), []);
  const stableError = useCallback((e) => errorRef.current && errorRef.current(e), []);

  // ===== Pantalla de resultado =====
  if (result) {
    return (
      <PaymentResult
        result={result}
        amount={amount}
        onRetry={() => setResult(null)}
        navigate={navigate}
        email={email}
        compraConSesion={Boolean(emailBloqueado)}
        nombreProducto={nombreProducto}
      />
    );
  }

  return <CheckoutLayout
    amount={amount}
    error={error}
    loading={loading}
    externalReference={externalReference}
    PUBLIC_KEY={PUBLIC_KEY}
    navigate={navigate}
    onBrickSubmit={stableSubmit}
    onBrickError={stableError}
    onBrickReady={stableReady}
    brickReady={brickReady}
    email={email}
    setEmail={setEmail}
    emailValid={emailValid}
    producto={producto}
    nombreProducto={nombreProducto}
    emailBloqueado={emailBloqueado}
    cotizacion={cotizacion}
    cotizando={cotizando}
  />;
}

function CheckoutLayout({
  amount, error, loading, externalReference, PUBLIC_KEY, navigate,
  onBrickSubmit, onBrickError, onBrickReady, brickReady,
  email, setEmail, emailValid,
  producto, nombreProducto, emailBloqueado, cotizacion, cotizando,
}) {
  const installmentValue = Math.round(amount / 3);
  const emailNormalizado = (email || '').trim().toLowerCase();
  // La cotizacion tiene que ser del mail que esta escrito AHORA: si lo cambian,
  // no se muestra el formulario de pago hasta tener el precio del nuevo.
  const cotizacionVigente = cotizacion && cotizacion.email === emailNormalizado ? cotizacion : null;
  const puedePagar = emailValid && cotizacionVigente && cotizacionVigente.estado === 'ok';
  const descuento = (cotizacionVigente && cotizacionVigente.descuento) || 0;

  return (
    <div className="min-h-screen bg-[#0D1B2A] text-white">
      {/* Header con logo */}
      <div className="px-4 py-4 border-b border-white/5 bg-[#0D1B2A]/95 backdrop-blur sticky top-0 z-20">
        <div className="max-w-5xl mx-auto flex items-center justify-between">
          <button
            onClick={() => navigate('/')}
            data-testid="checkout-back-btn"
            className="text-slate-400 hover:text-white text-sm flex items-center gap-1.5 transition-colors"
          >
            <ArrowLeft className="h-4 w-4" />
            <span className="hidden sm:inline">Volver</span>
          </button>
          <img
            src="/logo-evalpro-full.png"
            alt="EVALPRO"
            className="h-10 sm:h-12 w-auto object-contain"
            data-testid="checkout-header-logo"
          />
          <div className="flex items-center gap-1.5 text-xs text-slate-400">
            <Lock className="h-3.5 w-3.5 text-[#5B9B2F]" />
            <span className="hidden sm:inline">Pago seguro · MercadoPago</span>
            <span className="sm:hidden">Seguro</span>
          </div>
        </div>
      </div>

      {/* Banner de urgencia (countdown) */}
      <div className="bg-gradient-to-r from-[#5B9B2F]/15 via-[#5B9B2F]/10 to-[#5B9B2F]/15 border-b border-[#5B9B2F]/20">
        <div className="max-w-5xl mx-auto px-4 py-2.5 flex items-center justify-center gap-2 text-sm" data-testid="checkout-urgency-banner">
          <Timer className="h-4 w-4 text-[#5B9B2F]" />
          <span className="text-slate-300">
            Mantenemos tu acceso reservado por <ReservationCountdown />
          </span>
        </div>
      </div>

      <div className="max-w-5xl mx-auto px-4 py-8 lg:py-10">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 lg:gap-8">
          {/* Resumen del producto */}
          <aside className="lg:col-span-1 order-2 lg:order-1" data-testid="checkout-summary">
            <div className="bg-white/5 border border-white/10 rounded-2xl p-5 sticky top-24">
              <p className="text-xs uppercase tracking-wider text-slate-500 mb-2">Resumen</p>
              <h2 className="text-white font-semibold text-lg" data-testid="checkout-product-name">{nombreProducto}</h2>
              <p className="text-slate-400 text-sm mt-1">
                {producto === 'pack'
                  ? `${PACK_ENTREVISTAS} entrevistas iniciales. Los retests y las pruebas funcionales de esas personas no gastan. No vencen.`
                  : 'Acceso completo e ilimitado a la plataforma'}
              </p>
              {descuento > 0 && (
                <p className="text-[#5B9B2F] text-sm mt-2" data-testid="checkout-discount">
                  Descontamos {formatARS(descuento)} que ya pagaste en packs.
                </p>
              )}
              <div className="border-t border-white/10 my-4" />
              <div className="flex items-baseline justify-between">
                <span className="text-slate-400 text-sm">Total</span>
                <span className="text-white font-bold text-2xl" data-testid="checkout-total-amount">{formatARS(amount)}</span>
              </div>

              {/* 3 cuotas sin interés destacado */}
              <div className="mt-3 bg-[#5B9B2F]/10 border border-[#5B9B2F]/30 rounded-xl p-3" data-testid="installments-highlight">
                <div className="flex items-center gap-2 text-[#5B9B2F] text-sm font-semibold">
                  <CreditCard className="h-4 w-4" />
                  3 cuotas sin interés
                </div>
                <p className="text-slate-300 text-xs mt-1">
                  o 3 x <strong className="text-white">{formatARS(installmentValue)}</strong> con tu tarjeta de crédito
                </p>
              </div>

              <div className="mt-5 space-y-2 text-xs text-slate-400">
                <div className="flex items-center gap-2">
                  <ShieldCheck className="h-3.5 w-3.5 text-[#5B9B2F]" />
                  <span>Pago único, sin suscripción</span>
                </div>
                <div className="flex items-center gap-2">
                  <ShieldCheck className="h-3.5 w-3.5 text-[#5B9B2F]" />
                  <span>Garantía 10 días</span>
                </div>
                <div className="flex items-center gap-2">
                  <ShieldCheck className="h-3.5 w-3.5 text-[#5B9B2F]" />
                  <span>Acceso inmediato</span>
                </div>
              </div>

              {/* Logos métodos de pago */}
              <div className="mt-5 pt-4 border-t border-white/10">
                <p className="text-[10px] uppercase tracking-wider text-slate-500 mb-2">Aceptamos</p>
                <div className="flex items-center gap-2 flex-wrap">
                  <CardBadge label="Visa" />
                  <CardBadge label="Mastercard" />
                  <CardBadge label="Amex" />
                  <CardBadge label="Naranja" />
                </div>
              </div>
            </div>
          </aside>

          {/* Brick */}
          <main className="lg:col-span-2 order-1 lg:order-2">
            <h1 className="text-2xl sm:text-3xl font-bold text-white leading-tight mb-3">
              Estás a un paso de hacer la entrevista inicial con{' '}
              <span className="text-[#5B9B2F]">el rigor de un profesional.</span>
            </h1>
            <p className="text-slate-400 text-sm sm:text-base mb-6">
              Acceso inmediato. Y si no es para vos, tenés{' '}
              <span className="text-slate-200 font-semibold">10 días de garantía</span>.
            </p>

            {error && (
              <div className="bg-[#C83A2A]/10 border border-[#C83A2A]/30 rounded-xl p-4 mb-4 text-[#C83A2A] text-sm" data-testid="checkout-error">
                {error}
              </div>
            )}

            {loading && (
              <div className="flex items-center justify-center py-12 text-slate-400 text-sm">
                <Loader2 className="h-5 w-5 animate-spin mr-2" />
                Preparando checkout…
              </div>
            )}

            {/* Formulario unificado: email + Brick en un mismo scroll continuo.
                Sin gate ni botón "Continuar". El Brick se monta cuando el email es válido. */}
            {!loading && !error && externalReference && PUBLIC_KEY && (
              <>
                <div className="bg-white rounded-2xl overflow-hidden shadow-xl" data-testid="checkout-form-unified">
                  {/* Campo email — primer campo del formulario, siempre visible */}
                  <div className="p-4 sm:p-6 border-b border-slate-200">
                    <label htmlFor="checkout-email" className="flex items-center gap-2 text-[#0D1B2A] text-sm font-semibold mb-2">
                      <Mail className="h-4 w-4 text-[#5B9B2F]" />
                      Tu email
                    </label>
                    <Input
                      id="checkout-email"
                      type="email"
                      inputMode="email"
                      autoComplete="email"
                      placeholder="tu@email.com"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      readOnly={Boolean(emailBloqueado)}
                      data-testid="checkout-email-input"
                      className="h-12 bg-slate-50 border-slate-300 text-[#0D1B2A] placeholder:text-slate-400 focus-visible:ring-[#5B9B2F] read-only:opacity-70"
                    />
                    <p className="text-slate-500 text-xs mt-2">
                      {emailBloqueado
                        ? 'Es el mail de tu cuenta: lo que compres se suma ahí.'
                        : 'Te enviamos el comprobante a este email. Usá este mismo mail para crear tu cuenta.'}
                    </p>
                  </div>

                  {/* Brick — se monta cuando el email es sintácticamente válido */}
                  <div className="p-4 sm:p-6 relative" data-testid="payment-brick">
                    {emailValid && cotizacionVigente && cotizacionVigente.estado !== 'ok' ? (
                      <div className="py-8 flex flex-col items-center justify-center text-center" data-testid="checkout-blocked">
                        <p className="text-[#0D1B2A] text-sm max-w-sm leading-relaxed">
                          {cotizacionVigente.mensaje}
                        </p>
                        <Button
                          onClick={() => navigate('/login')}
                          data-testid="checkout-blocked-login"
                          className="mt-5 h-11 px-6 bg-[#4A8126] hover:bg-[#3D6A1F] text-white font-semibold rounded-xl"
                        >
                          Iniciar sesión
                        </Button>
                      </div>
                    ) : puedePagar ? (
                      <>
                        {!brickReady && (
                          <div
                            className="absolute inset-0 bg-white/95 backdrop-blur-sm flex flex-col items-center justify-center z-10 px-6"
                            data-testid="brick-loading-overlay"
                            role="status"
                            aria-live="polite"
                          >
                            <Loader2 className="h-8 w-8 text-[#5B9B2F] animate-spin mb-4" />
                            <p className="text-[#0D1B2A] font-semibold text-base text-center">
                              Cargando tu forma de pago segura...
                            </p>
                            <p className="text-slate-500 text-xs text-center mt-2 max-w-xs">
                              Conectando con MercadoPago · Esto puede tardar unos segundos
                            </p>
                            <div className="mt-5 flex items-center gap-2 text-[10px] text-slate-500">
                              <Lock className="h-3 w-3" />
                              <span>Conexión cifrada SSL · PCI DSS Level 1</span>
                            </div>
                          </div>
                        )}
                        <MemoizedPaymentBrick
                          amount={amount}
                          email={email}
                          onSubmit={onBrickSubmit}
                          onError={onBrickError}
                          onReady={onBrickReady}
                        />
                      </>
                    ) : (
                      <div className="py-12 flex flex-col items-center justify-center text-center" data-testid="brick-waiting-email">
                        <div className="h-10 w-10 rounded-full bg-slate-100 flex items-center justify-center mb-3">
                          <Mail className="h-5 w-5 text-slate-400" />
                        </div>
                        <p className="text-slate-500 text-sm">
                          {emailValid && cotizando
                            ? 'Calculando el precio…'
                            : 'Ingresá tu email para ver las formas de pago disponibles'}
                        </p>
                      </div>
                    )}
                  </div>
                </div>
              </>
            )}

            {!PUBLIC_KEY && (
              <div className="bg-[#C83A2A]/10 border border-[#C83A2A]/30 rounded-xl p-4 text-[#C83A2A] text-sm">
                MercadoPago no está configurado (falta REACT_APP_MERCADOPAGO_PUBLIC_KEY).
              </div>
            )}

            {/* Trust footer */}
            <div className="mt-6 flex items-center justify-center gap-4 text-[11px] text-slate-500" data-testid="checkout-trust-footer">
              <span className="flex items-center gap-1">
                <Lock className="h-3 w-3" /> Cifrado SSL
              </span>
              <span>·</span>
              <span>Procesado por MercadoPago</span>
              <span>·</span>
              <span>PCI DSS Level 1</span>
            </div>
          </main>
        </div>
      </div>
    </div>
  );
}

function CardBadge({ label }) {
  return (
    <span className="inline-flex items-center px-2 py-1 rounded-md bg-white/10 border border-white/15 text-[11px] text-slate-300 font-medium">
      {label}
    </span>
  );
}

function PaymentResult({ result, amount, onRetry, navigate, email, compraConSesion, nombreProducto }) {
  const { status, status_detail, payment_id } = result;
  // Quien compra sin sesion todavia no tiene cuenta (o la tiene y no inicio
  // sesion): se lo manda a crearla con el mail ya cargado. Antes este boton
  // mandaba al login, que rechaza a quien no tiene cuenta.
  const irDespuesDeComprar = compraConSesion
    ? () => navigate('/alumnos')
    : () => navigate(`/register?email=${encodeURIComponent((email || '').trim().toLowerCase())}`);

  const variants = {
    approved: {
      Icon: CheckCircle2,
      iconClass: 'text-[#5B9B2F] bg-[#5B9B2F]/10',
      title: '¡Pago aprobado!',
      msg: compraConSesion
        ? `Tu compra de ${nombreProducto} ya está en tu cuenta.`
        : `Tu compra de ${nombreProducto} fue confirmada. Creá tu cuenta con este mismo mail para empezar.`,
      cta: compraConSesion ? 'Volver a EVALPRO' : 'Crear mi cuenta',
      onCta: irDespuesDeComprar,
    },
    in_process: {
      Icon: Clock,
      iconClass: 'text-amber-500 bg-amber-500/10',
      title: 'Pago en revisión',
      msg: 'MercadoPago está validando tu pago. Te enviaremos un mail apenas se acredite.',
      cta: 'Volver al inicio',
      onCta: () => navigate('/'),
    },
    pending: {
      Icon: Clock,
      iconClass: 'text-amber-500 bg-amber-500/10',
      title: 'Pago pendiente',
      msg: 'Esperamos la confirmación del pago. Te avisaremos por mail apenas se acredite.',
      cta: 'Volver al inicio',
      onCta: () => navigate('/'),
    },
    rejected: {
      Icon: XCircle,
      iconClass: 'text-[#C83A2A] bg-[#C83A2A]/10',
      title: 'Pago rechazado',
      msg: 'No pudimos procesar tu pago. Probá con otra tarjeta o contactá a tu banco.',
      cta: 'Volver a intentar',
      onCta: onRetry,
    },
    error: {
      Icon: XCircle,
      iconClass: 'text-[#C83A2A] bg-[#C83A2A]/10',
      title: 'Algo salió mal',
      msg: status_detail || 'No pudimos procesar la solicitud. Intentá de nuevo en unos minutos.',
      cta: 'Volver a intentar',
      onCta: onRetry,
    },
  };

  const v = variants[status] || variants.error;
  const { Icon } = v;

  return (
    <div className="min-h-screen bg-[#0D1B2A] text-white flex items-center justify-center px-4">
      <div className="max-w-md w-full text-center" data-testid={`payment-result-${status}`}>
        <div className={`mx-auto h-16 w-16 rounded-full flex items-center justify-center ${v.iconClass}`}>
          <Icon className="h-8 w-8" />
        </div>
        <h1 className="text-2xl font-bold text-white mt-5" data-testid="payment-result-title">{v.title}</h1>
        <p className="text-slate-400 mt-2 leading-relaxed">{v.msg}</p>

        {status === 'approved' && (
          <div className="mt-6 bg-white/5 border border-white/10 rounded-xl p-4 text-left text-sm">
            <div className="flex justify-between py-1"><span className="text-slate-400">Monto</span><span className="text-white font-semibold">{formatARS(amount)}</span></div>
            <div className="flex justify-between py-1"><span className="text-slate-400">ID de pago</span><span className="text-white font-mono text-xs">{payment_id}</span></div>
          </div>
        )}

        <Button
          onClick={v.onCta}
          data-testid="payment-result-cta"
          className="mt-6 h-12 px-8 bg-[#5B9B2F] hover:bg-[#4a8126] text-white font-bold rounded-xl"
        >
          {v.cta}
        </Button>

        {status === 'approved' && !compraConSesion && (
          <p className="mt-4 text-sm text-slate-400">
            ¿Ya tenés cuenta?{' '}
            <button
              type="button"
              onClick={() => navigate('/login')}
              className="text-white underline underline-offset-4"
              data-testid="payment-result-login"
            >
              Iniciá sesión
            </button>
            : lo que compraste ya está ahí.
          </p>
        )}
      </div>
    </div>
  );
}
